import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { Trash2, RotateCcw, Clock, AlertTriangle, Search, Package } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';

const TABS = [
  { key: 'fields', label: 'Fields', icon: '🏷️' },
  { key: 'screens', label: 'Screens', icon: '📱' },
  { key: 'records', label: 'Records', icon: '📁' },
  { key: 'workflows', label: 'Workflows', icon: '⚡' },
  { key: 'record_types', label: 'Record Types', icon: '📋' },
];

export default function RecycleBin() {
  const navigate = useNavigate();
  const [data, setData] = useState({ fields: [], screens: [], records: [], workflows: [], record_types: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fields');
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [purging, setPurging] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/recycle-bin');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRestore = async (type, id, name) => {
    setRestoring(id);
    try {
      await api.post('/admin/recycle-bin/restore', { type, id });
      toast.success(`"${name}" restored`);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.includes('already exists')) {
        const choice = window.confirm(
          `Cannot restore "${name}" — an active item with the same name already exists.\n\nWould you like to restore it with a different name ("${name} (Restored)")?`
        );
        if (choice) {
          try {
            await api.post('/admin/recycle-bin/restore', { type, id, rename: true });
            toast.success(`"${name}" restored with new name`);
            fetchData();
          } catch (err2) {
            toast.error(err2.response?.data?.error || 'Restore failed');
          }
        }
      } else {
        toast.error(msg || 'Restore failed');
      }
    } finally {
      setRestoring(null);
    }
  };

  const handlePurge = async (type, id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setPurging(id);
    try {
      await api.delete('/admin/recycle-bin/purge', { data: { type, id } });
      toast.success('Permanently deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally {
      setPurging(null);
    }
  };

  const handleEmptyAll = async () => {
    if (!window.confirm('Permanently delete ALL items in the recycle bin? This cannot be undone.')) return;
    try {
      await api.delete('/admin/recycle-bin/empty?force=true');
      toast.success('Recycle bin emptied');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to empty');
    }
  };

  const totalCount = data.fields.length + data.screens.length + data.records.length + data.workflows.length + (data.record_types || []).length;
  const items = (data[activeTab] || []).filter(item =>
    !search || (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.record_type_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const singularType = activeTab.replace(/s$/, '');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getDaysColor = (days) => {
    if (days <= 7) return 'text-red-600 bg-red-50';
    if (days <= 14) return 'text-amber-600 bg-amber-50';
    return 'text-gray-500 bg-gray-50';
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Recycle Bin" description={`${totalCount} deleted ${totalCount === 1 ? 'item' : 'items'} — auto-purged after 60 days`} icon={Trash2} iconColor="text-gray-600" iconBg="bg-gray-100">
        {totalCount > 0 && (
          <button onClick={handleEmptyAll}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
            <Trash2 className="h-4 w-4" /> Empty All
          </button>
        )}
      </AdminPageHeader>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl">
        {TABS.map(tab => {
          const count = (data[tab.key] || []).length;
          return (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key ? 'bg-brand-100 text-brand-700' : 'bg-gray-200 text-gray-600'
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      {items.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search deleted ${activeTab}...`}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-400" />
        </div>
      )}

      {/* Items */}
      {totalCount === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Recycle bin is empty</h3>
          <p className="text-sm text-gray-500">Deleted items will appear here for 60 days before being permanently removed.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500">{search ? 'No matching items' : `No deleted ${activeTab}`}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider rounded-t-xl">
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Record Type</div>
            <div className="col-span-2">Deleted</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {items.map(item => (
            <div key={item.id} className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center hover:bg-gray-50 transition-colors">
              <div className="col-span-4">
                <span className="text-sm font-medium text-gray-900">{item.name || 'Untitled'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                  {item.subtype || singularType}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-xs text-gray-500">{item.record_type_name || '—'}</span>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-gray-500">{formatDate(item.deleted_at)}</div>
                <div className={`text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${getDaysColor(item.days_remaining)}`}>
                  <Clock className="h-3 w-3" />
                  {item.days_remaining}d left
                </div>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1.5">
                <button
                  onClick={() => handleRestore(singularType, item.id, item.name)}
                  disabled={restoring === item.id}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 disabled:opacity-50 transition-colors">
                  <RotateCcw className="h-3.5 w-3.5" />
                  {restoring === item.id ? '...' : 'Restore'}
                </button>
                <button
                  onClick={() => handlePurge(singularType, item.id, item.name)}
                  disabled={purging === item.id}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                  {purging === item.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
